<template lang='pug'>
.date-dropdown
	.arrow.highlight-background
	.calendar-body.highlight-background
		.month-year-select
			p.month(
				:tabindex='open ? 0 : null'
				@click='showMonthSelect = !showMonthSelect'
				@keydown.enter='showMonthSelect = !showMonthSelect'
			) {{ months[activeMonth] }}
			.year
				.year-arrow.back(
					:tabindex='open ? 0 : null'
					@click='activeYear -= 1'
					@keydown.enter='activeYear -= 1'
				)
				p {{ activeYear }}
				.year-arrow.forward(
					:tabindex='open ? 0 : null'
					@click='activeYear += 1'
					@keydown.enter='activeYear += 1'
				)
		.month-select.highlight-background(:class='{ open : showMonthSelect }')
			p(
				v-for='( month, i ) in months'
				:tabindex='showMonthSelect ? 0 : null'
				:class='{ active : i === activeMonth, "highlight-text" : i === activeMonth }'
				@click='selectMonth( i )'
				@keydown.enter='selectMonth( i )'
			) {{ month }}
		.days
			.day.week-label(v-for='d in ["S","M","T","W","T","F","S"]')
				span {{ d }}
			.day(
				v-for='( day, i ) in displayedDays'
				:tabindex='open ? 0 : null'
				:class='{\
					active         : day.month === activeMonth, \
					start          : is( "startingDay", day ),\
					"start-before" : ( i === 0 && dayIsEarlier( startingDay, day ) ),\
					end            : is( "endingDay", day ),\
					today          : is( "today", day ),\
					"has-end"      : Object.keys( endingDay ).length && ( is( "startingDay", day ) || ( i === 0 && dayIsEarlier( startingDay, day ) && dayIsEarlier( day, endingDay ) ) ), \
				}'
				@click='select( day )'
				@keydown.enter='select( day )'
			)
				span(:class='{ "highlight-text" : isHighlighted( day ) }') {{ day.date }}
		.buttons(v-if='clearable')
			.button(
				:tabindex='open ? 0 : null'
				@click='done'
				@keydown.enter='done'
			) Done
			.button.white.highlight-text(
				:tabindex='open ? 0 : null'
				@click='clear'
				@keydown.enter='clear'
			) Clear
</template>

<script>
import {
	months,
	getDaysInMonth,
	getLastMonth,
	getNextMonth
} from '@/lib/time';
import { Compare } from '@/lib/utils';

const today = new Date();

export default {
	name : 'date-dropdown',

	props : {

		range : {
			type : Boolean,
		},

		clearable : {
			type    : Boolean,
			default : false,
		},

		open : {
			type : Boolean,
		},

		parentVal : {
			type : [String, Object],
		},

	},

	data : () => ( {
		months,

		activeMonth : today.getMonth(),
		activeDate  : today.getDate(),
		activeYear  : today.getFullYear(),

		today : {
			date  : today.getDate(),
			month : today.getMonth(),
			year  : today.getFullYear(),
		},

		dateSelected : false,

		startingDay : {},
		endingDay   : {},

		showMonthSelect : false,

		datePattern      : new RegExp( /\d{1,2}\/\d{1,2}\/(\d{2}|\d{4})/ ),
		dateRangePattern : new RegExp( /.+\/.+\/.+\-.+\/.+\/.+/ ), // eslint-disable-line

	} ),

	computed : {

		daysInActiveMonth() {

			const { activeMonth, activeYear } = this;

			return getDaysInMonth( activeMonth, activeYear );

		},

		displayedDays() {
			const { daysInActiveMonth, activeMonth, activeYear } = this;

			// determine the months information
			const monthName          = months[activeMonth];
			const startingDayOfMonth = new Date( `${monthName} 1, ${activeYear}` );
			const endingDayOfMonth   = new Date( `${monthName} ${daysInActiveMonth}, ${activeYear}` );

			// find how many days are at the beginning
			// of the calendar (e.g. the 29, 30, and 31
			// of march when displaying April)
			const daysAtBeginning = ( () => {
				const days = [];

				// get the month and year of last
				// month (which could be a year behind
				// our current year if our active month
				// is January)
				const {
					month : lastMonth,
					year  : lastYear,
				} = getLastMonth( activeMonth, activeYear );

				// find how many days were in last month
				// ? This function accounts for leap years
				const daysLastMonth     = getDaysInMonth( lastMonth, lastYear );
				const startingDayOfWeek = startingDayOfMonth.getDay();

				// add in as many days at the beginning as
				// we need
				for ( let i = 1; i <= startingDayOfWeek; i += 1 ) {
					days.push( {
						month : lastMonth,
						date  : ( daysLastMonth - startingDayOfWeek + i ),
						year  : lastYear,
					} );
				}

				return days;
			} )();

			const daysInMonth = ( () => {
				const daysThisMonth = getDaysInMonth( activeMonth, activeYear );
				const days          = [];

				for ( let i = 0; i < daysThisMonth; i += 1 ) {

					days.push( {
						month : activeMonth,
						date  : i + 1,
						year  : activeYear,
					} );

				}

				return days;
			} )();

			const daysAtEnd = ( () => {
				const days = [];

				// get the month and year of next
				// month (which could be a year ahead
				// our current year if our active month
				// is December)
				const {
					month : nextMonth,
					year  : nextYear,
				} = getNextMonth( activeMonth, activeYear );

				// determine the ending day of the week
				const endingDayOfWeek = endingDayOfMonth.getDay();

				// add in as many days at the end as
				// we need
				for ( let i = 1; i <= 7 - ( endingDayOfWeek + 1 ); i += 1 ) {
					days.push( {
						month : nextMonth,
						date  : i,
						year  : nextYear,
					} );
				}

				return days;
			} )();

			return daysAtBeginning
				.concat( daysInMonth )
				.concat( daysAtEnd );
		},

		dateRange() {
			if ( !this.range || !this.dateSelected ) {
				return '';
			}

			const {
				startingDay,
				endingDay,
			} = this;


			if ( Object.keys( endingDay ).length ) {
				const range = {};

				range.after  = this.getDate( '00:00:00', startingDay );
				range.before = this.getDate( '23:59:59', endingDay );

				return range;
			}

			if ( Object.keys( startingDay ).length ) {
				return this.getDate( '00:00:00', startingDay );
			}

			return '';
		}

	},

	watch : {

		startingDay : {
			deep : true,
			handler( day ) {
				this.$emit( 'start-date-changed', day );

				if ( this.range ) {
					this.$emit( 'range-changed', this.dateRange );
				}
			}
		},

		endingDay : {
			deep : true,
			handler( day ) {
				if ( this.range ) {
					this.$emit( 'end-date-changed', day );
					this.$emit( 'range-changed', this.dateRange );
				}
			}
		},

		parentVal : {
			deep : true,
			handler( date ) {

				if ( !date ) {
					this.clear();
					return;
				}

				const { range } = this;

				// if it's a range, check if the value is equal to the
				// date range pattern, if it is select the before and after
				// if it's not, see if maybe it's a regular date and we can
				// at least select the before.

				// if it's not even a range, then we will just skip to checking
				// if it's a regular date. If it's not, do nothing.
				if ( range ) {

					if ( date.includes( '-' ) ) {

						const splitDates = date.split( '-' );

						const [beforeDateString, afterDateString] = splitDates;

						const before = {};
						const after  = {};

						const beforeDate = new Date( beforeDateString );
						const afterDate  = new Date( afterDateString );

						before.date  = beforeDate.getDate();
						before.month = beforeDate.getMonth();
						before.year  = beforeDate.getFullYear();

						after.date  = afterDate.getDate();
						after.month = afterDate.getMonth();
						after.year  = afterDate.getFullYear();

						this.clear();

						this.select( before );
						this.select( after );

						return;

					}

					if ( !!new Date( date ).getTime() ) { // eslint-disable-line

						const formattedDate = {};

						const dateObj = new Date( date );

						formattedDate.date  = dateObj.getDate();
						formattedDate.month = dateObj.getMonth();
						formattedDate.year  = dateObj.getFullYear();

						this.clear();

						this.select( formattedDate );
						return;
					}

				}

				if ( !!new Date( date ).getTime() ) { // eslint-disable-line

					const formattedDate = {};

					const dateObj = new Date( date );

					formattedDate.date  = dateObj.getDate();
					formattedDate.month = dateObj.getMonth();
					formattedDate.year  = dateObj.getFullYear();

					this.select( formattedDate );

				}

			}
		}

	},

	methods : {

		is( prop, d ) {
			return Compare( d, this[prop] );
		},

		isHighlighted( day ) {
			if ( this.is( 'startingDay', day ) ) {
				return true;
			}

			if ( this.is( 'endingDay', day ) ) {
				return true;
			}

			if ( this.dayIsEarlier( day, this.endingDay ) && this.dayIsEarlier( this.startingDay, day ) ) {
				return true;
			}

			return false;
		},

		selectDateString( date ) {

			const formattedDate = {};

			const dateObj = new Date( date );

			formattedDate.date  = dateObj.getDate();
			formattedDate.month = dateObj.getMonth();
			formattedDate.year  = dateObj.getFullYear();

			this.select( formattedDate );
			this.selectMonth( formattedDate.month );
			this.activeYear = formattedDate.year;

		},

		select( d ) {
			this.dateSelected = true;

			if ( !this.range ) {
				this.startingDay = d;

				return;
			}

			if ( !Object.keys( this.startingDay ).length ) {
				this.startingDay = d;

				return;
			}

			if ( this.dayIsEarlier( d, this.startingDay ) ) {
				if ( !Object.keys( this.endingDay ).length ) {
					this.endingDay = this.startingDay;
				}

				this.startingDay = d;

				return;
			}

			if ( this.dayIsEarlier( this.endingDay, d ) ) {
				this.endingDay = d;

				return;
			}

			if ( Compare( this.startingDay, d ) ) {

				if ( Object.keys( this.endingDay ).length ) {
					this.startingDay = this.endingDay;
					this.endingDay   = {};
				}

				else {
					this.startingDay = {};
				}

				return;
			}

			if ( Compare( this.endingDay, d ) ) {
				this.endingDay = {};

				return;
			}

			this.endingDay = d;
		},

		selectMonth( i ) {
			this.activeMonth = i;
			this.showMonthSelect = false;
		},

		dayIsEarlier( dayOne, dayTwo ) {
			// loop through each of these and if
			// any are before dayTwo, return true
			const compares = ['year', 'month', 'date'];

			for ( let i = 0; i < compares.length; i += 1 ) {
				const prop = compares[i];

				if ( dayOne[prop] > dayTwo[prop] ) {
					return false;
				}

				if ( dayOne[prop] < dayTwo[prop] ) {
					return true;
				}
			}

			return false;
		},

		getDate( time, day ) {
			const {
				date,
				month,
				year
			} = day;

			return new Date( `${this.months[month]} ${date}, ${year} ${time} GMT-0000` ).toISOString();
		},

		done() {
			this.$emit( 'done' );
		},

		clear() {
			this.activeMonth = new Date().getMonth();
			this.activeDate  = new Date().getDate();
			this.activeYear  = new Date().getFullYear();
			this.endingDay   = {};
			this.startingDay = {};
			this.today       = {};
			this.$emit( 'clear' );
		}

	},
};
</script>

<style lang='scss'>
.date-dropdown {
	position: relative;

	.arrow {
		height: 30px;
		width: 60px;
		position: absolute;
		left: 50%;
		top: 0;
		transform: translate(-50%,-100%);
		z-index: 2;
		overflow: hidden;
		pointer-events: none;

		&::before {
			content: ' ';
			position: absolute;
			top: 90%;
			left: 50%;
			border-radius: 3px;
			height: 40px;
			width: 40px;
			background: $color-primary;
			box-shadow: $inputBoxShadow;
			transform: translateX(-50%) rotate(45deg);
		}
	}

	.calendar-body {
		border-radius: 10px;
		box-shadow: $inputBoxShadow;
		background: $color-primary;

		.month-year-select {
			display: flex;
			padding: 10px;
			padding-bottom: 0;
			align-items: center;
			justify-content: space-between;

			p {
				color: white;
				font-size: 14px;

				&.month {
					padding-right: 15px;
					position: Relative;
					padding-left: 3px;

					&::before, &::after {
						content: ' ';
						height: 6px;
						width: 2px;
						background: white;
						border-radius: 12px;
						right: 0px;
						position: absolute;
						transition: .2s ease-in-out;
						top: 50%;
						transform: translate3d( 0, -50%, 0 );
						z-index: 2;
					}

					&::before {
						transform: translate3d(0px, -50%, 0) rotate(45deg);
					}

					&::after {
						transform: translate3d(-3px, -50%, 0) rotate(-45deg);
					}
				}
			}

			.year {
				display: flex;
				align-items: center;

				.year-arrow {
					position: relative;
					height: 20px;
					width: 20px;
					opacity: 0.8;

					&:hover {
						opacity: 1;
					}

					&.back {
						transform: rotate(90deg);
					}

					&.forward {
						transform: rotate(-90deg);
					}

					&::before, &::after {
						content: ' ';
						height: 6px;
						width: 2px;
						background: white;
						border-radius: 12px;
						right: calc(50% - 3px);
						position: absolute;
						transition: .2s ease-in-out;
						top: 50%;
						transform: translate3d( 0, -50%, 0 );
						z-index: 2;
					}

					&::before {
						transform: translate3d(0px, -50%, 0) rotate(45deg);
					}

					&::after {
						transform: translate3d(-3px, -50%, 0) rotate(-45deg);
					}
				}
			}
		}

		.month-select {
			position: absolute;
			height: 100%;
			width: 100%;
			overflow: auto;
			background: $color-primary;
			transition: opacity 0.2s ease;
			pointer-events: all;
			top: 0;
			left: 0;
			z-index: 2;
			border-radius: 10px;
			padding: 10px 0;

			&::-webkit-scrollbar-thumb {
				background-color: white;
			}

			&:not(.open) {
				opacity: 0;
				pointer-events: none;
			}

			p {
				color: white;
				font-size: 14px;
				padding: 3px 10px;
				cursor: pointer;

				&.active {
					background: white;
					color: $color-primary;
				}
			}
		}

		.days {
			display: flex;
			flex-wrap: wrap;
			width: 195px; // 20px padding + ( 7 * 25px ) days
			padding: 10px;

			.day {
				flex: 0 0 calc(100% / 7);
				height: 25px;
				font-size: 12px;
				cursor: pointer;
				position: relative;
				display: flex;
				align-items: center;
				justify-content: center;

				&:not(.week-label) {

					&::before {
						content : ' ';
						position: absolute;
						top: 50%;
						left: 50%;
						transform: translate(-50%,-50%);
						height: calc(100% - 2px);
						width: calc(100% - 2px);
						border-radius: 50%;
						background: white;
						opacity: 0;
						z-index: 0;
					}

					&:hover::before {
						opacity: 0.25;
					}

					&:not(.active) {

						span {
							opacity: 0.6;
						}

						&::before {
							background: #cfe3cd;
						}
					}

					&.start-before.has-end::before {
						border-top-left-radius: 0 !important;
						border-bottom-left-radius: 0 !important;
					}

					&.start,
					&.start-before.has-end {

						&.today::before {
							border: none;
							background: white;
						}

						&::before {
							opacity: 1;
						}

						span {
							color: $color-primary;
						}

						&.has-end {

							&::before {
								width: 100%;
								border-top-right-radius: 0;
								border-bottom-right-radius: 0;
								border-top-left-radius: 15px;
								border-bottom-left-radius: 15px;
							}

							&.start-before,
							~ .day {

								&:nth-child(7n),
								&:nth-child(7n - 6) {

									&::before {
										width: calc(100% + 10px);
									}
								}

								&:nth-child(7n) {

									&.end::before {
										width: 100%;
										transform: translate(0,-50%);
									}

									&::before {
										right: 0;
										left: unset;
										transform: translate(10px, -50%);
									}
								}

								&:nth-child(7n - 6)::before {
									left: 0;
									transform: translate(-10px, -50%);
									width: calc(100% + 11px);
								}
							}

							~ .day {

								&::before {
									width: 100%;
									border-radius: 0;
									opacity: 1;
								}

								&.today::before {
									border: none;
									background: white;
								}

								span {
									color: $color-primary;
									opacity: 1;
								}

								&.end,
								~ .end {

									&::before {
										border-top-right-radius: 15px;
										border-bottom-right-radius: 15px;
									}

									~ .day.today {

										&::before {
											border-radius: 50%;
											height: calc(100% - 2px);
											width: calc(100% - 2px);
											background: transparent;
											border: 2px solid white;
										}

										span {
											color: white;
										}
									}

									~ .day:not(.today) {

										&::before {
											opacity: 0;
										}

										span {
											color: white;
										}
									}
								}
							}
						}
					}

					&.end {

						&::before {
							opacity: 1;
						}

						span {
							color: $color-primary;
						}
					}

					&.today {

						&::before {
							opacity: 1;
							background: transparent;
							border: 2px solid white;
						}
					}
				}

				&.week-label {

					span {
						font-weight: 600;
					}
				}

				span {
					color: white;
					z-index: 1;
					position: relative;
				}
			}
		}

		.buttons {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			border-bottom-right-radius: 10px;
			border-bottom-left-radius: 10px;
			overflow: hidden;

			.button {
				padding: 10px;
				border-radius: 0;
				font-size: 12px;
				min-width: 0;
				flex: 1 1 0;
				background: rgba(255,255,255,0.2);
				transition: background 0.1s ease, opacity 0.1s ease;

				&:active {
					background: rgba(255,255,255,0.4);
				}

				&:not(:hover) {
					box-shadow: none;
				}

				&.white {
					background: white;
					color: $color-primary;

					&:active {
						opacity: 0.6;
					}
				}
			}
		}
	}
}
</style>
